// Транспорт для вызовов бэкенда.
//
// В dev-режиме Vite проксирует /api на Kestrel, в сборке статику и API отдаёт один хост.
// Поэтому адреса везде относительные, базовый URL не нужен.
//
// Сетевой сбой и отказ сервера транспорт переживает сам: он повторяет запрос несколько раз
// с растущими паузами. Отказ по вине запроса не повторяется — сам он не исправится.

/** Ответ бэкенда с кодом ошибки. Отличает отказ сервера от сетевого сбоя. */
export class ApiError extends Error {
  constructor(
    readonly url: string,
    readonly status: number,
  ) {
    super(`Запрос ${url} завершился с кодом ${status}`);
    this.name = 'ApiError';
  }
}

/**
 * Паузы перед повторами в миллисекундах. Сколько пауз, столько и повторов.
 *
 * Паузы растут: короткий сбой сети переживает первый повтор, а упавший бэкенд получает
 * время подняться. Дольше ждать нет смысла, дальше игроку надо сказать, что связи нет.
 */
export const RETRY_DELAYS: readonly number[] = [500, 1000, 2000];

/** Ждёт заданное время. */
function wait(ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Стоит ли повторять запрос после этой ошибки.
 *
 * Сетевой сбой и отказ сервера проходят сами, поэтому повтор им помогает. Ответ 4xx говорит,
 * что запрос неверный: повторять его бессмысленно, ответ будет тот же.
 */
function worthRetry(error: unknown): boolean {
  return !(error instanceof ApiError) || error.status >= 500;
}

/**
 * Читает JSON по адресу и отдаёт его как T.
 *
 * Схема не проверяется: тип описывает контракт бэкенда, а не подтверждает его.
 *
 * @throws ApiError если сервер ответил кодом ошибки
 */
async function readJson<T>(url: string, method: string): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      const response: Response = await fetch(url, { method, headers: { accept: 'application/json' } });
      if (!response.ok)
        throw new ApiError(url, response.status);
      return (await response.json()) as T;
    } catch (error: unknown) {
      if (attempt >= RETRY_DELAYS.length || !worthRetry(error))
        throw error;

      console.warn(`Запрос ${url} не удался, повтор через ${RETRY_DELAYS[attempt]} мс`, error);
      await wait(RETRY_DELAYS[attempt]);
    }
  }
}

/**
 * Читает JSON по адресу.
 *
 * @throws ApiError если сервер ответил кодом ошибки
 */
export function getJson<T>(url: string): Promise<T> {
  return readJson<T>(url, 'GET');
}

/**
 * Просит сервер сделать действие и читает ответ как JSON.
 *
 * Тела в запросе нет: параметры игра передаёт в адресе.
 *
 * @throws ApiError если сервер ответил кодом ошибки
 */
export function postJson<T>(url: string): Promise<T> {
  return readJson<T>(url, 'POST');
}
